import '../operations.css';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { analyticsApi } from '../../api/analytics';
import type { ComparisonSlice, RepeatClickTrendPoint } from '../../api/analytics';
import { ReportingComparison } from './ReportingComparison';

const WINDOW_OPTIONS = [7, 30, 90] as const;

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatMedianSeconds(seconds: number | null): string {
  if (seconds === null) return 'N/A';
  const hours = seconds / 3600;
  if (hours >= 1) return `${hours.toFixed(1)}h`;
  const minutes = seconds / 60;
  if (minutes >= 1) return `${minutes.toFixed(1)}m`;
  return `${seconds.toFixed(0)}s`;
}

function sliceColumns() {
  return [
    {
      key: 'label',
      header: 'Name',
      render: (s: ComparisonSlice) => <span className="font-medium text-gray-900">{s.label}</span>,
    },
    {
      key: 'recipient_count',
      header: 'Recipients',
      render: (s: ComparisonSlice) => s.recipient_count,
    },
    {
      key: 'report_rate',
      header: 'Report Rate',
      render: (s: ComparisonSlice) => `${formatPercent(s.report_rate)} (${s.reported_count}/${s.recipient_count})`,
    },
    {
      key: 'median_report_seconds',
      header: 'Median Time to Report',
      render: (s: ComparisonSlice) => formatMedianSeconds(s.median_report_seconds),
    },
    {
      key: 'missing_report_time_count',
      header: 'Missing Report Time',
      render: (s: ComparisonSlice) => s.missing_report_time_count,
    },
    {
      key: 'clicked_recipient_count',
      header: 'Clicked',
      render: (s: ComparisonSlice) => s.clicked_recipient_count,
    },
    {
      key: 'repeat_click_recipient_count',
      header: 'Repeat Clickers',
      render: (s: ComparisonSlice) => s.repeat_click_recipient_count,
    },
    {
      key: 'suspected_scanner_click_count',
      header: 'Suspected Scanner Clicks',
      render: (s: ComparisonSlice) => s.suspected_scanner_click_count,
    },
  ];
}

const trendColumns = [
  { key: 'date', header: 'Date' },
  {
    key: 'repeat_click_recipients',
    header: 'Repeat-Click Recipients',
    render: (p: RepeatClickTrendPoint) => p.repeat_click_recipients,
  },
  {
    key: 'repeat_click_events',
    header: 'Repeat-Click Events',
    render: (p: RepeatClickTrendPoint) => p.repeat_click_events,
  },
  {
    key: 'suspected_scanner_clicks',
    header: 'Suspected Scanner Clicks',
    render: (p: RepeatClickTrendPoint) => p.suspected_scanner_clicks,
  },
];

export function ComparisonAnalytics() {
  const [windowDays, setWindowDays] = useState<number>(30);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-comparison', windowDays],
    queryFn: async () => {
      const res = await analyticsApi.getComparison(undefined, windowDays);
      return res.data;
    },
  });

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="operations-eyebrow">Behavior intelligence</p>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Comparison</h1>
          <p className="text-gray-500 mt-1">
            Compare report rates, time-to-report, and repeat-click behavior across campaigns and cohorts
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 mr-1">Window:</span>
          {WINDOW_OPTIONS.map((days) => (
            <Button
              key={days}
              variant={windowDays === days ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setWindowDays(days)}
            >
              {days}d
            </Button>
          ))}
        </div>
      </div>

      {isError ? (
        <p role="alert" className="text-red-700">
          Could not load comparison analytics. Refresh the page to try again.
        </p>
      ) : isLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded"></div>
          ))}
        </div>
      ) : data ? (
        <>
          <ReportingComparison cohorts={data.cohorts} />
          <Card>
            <p className="text-sm text-gray-500">
              {new Date(data.window.start).toLocaleDateString()} &ndash;{' '}
              {new Date(data.window.end).toLocaleDateString()} ({data.window.days} days)
            </p>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">By Campaign</h3>
            <Table
              columns={sliceColumns()}
              data={data.campaigns}
              keyExtractor={(s) => s.key}
              emptyMessage="No campaigns in this window."
            />
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">By Cohort</h3>
            <Table
              columns={sliceColumns()}
              data={data.cohorts}
              keyExtractor={(s) => s.key}
              emptyMessage="No cohorts in this window."
            />
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Repeat-Click Trend</h3>
            <Table
              columns={trendColumns}
              data={data.repeat_click_trend}
              keyExtractor={(p) => p.date}
              emptyMessage="No repeat-click activity in this window."
            />
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Definitions</h3>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
              <li>{data.definitions.recipient_denominator}</li>
              <li>{data.definitions.report_attribution}</li>
              <li>{data.definitions.median_time_to_report}</li>
              <li>{data.definitions.repeat_click}</li>
              <li>{data.definitions.missing_data}</li>
              <li>{data.definitions.scanner_label}</li>
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}
